import { atom } from 'jotai';

import { convertUnit, DisplayUnit, Unit } from '@core';
import { CustomCorridor, FullLayout, LayoutCustomization, ParameterNode } from '@data';

import { parametersMapAtom, swingMeasuredParametersMapAtom } from '../global/uiNodes';

import { activeUserSettingsAtom } from './user';

export const layoutAtom = atom<FullLayout>();

export const layoutCustomizationsAtom = atom<LayoutCustomization[]>((get) => get(layoutAtom)?.customizations || []);
export const layoutCustomCorridorsAtom = atom<CustomCorridor[]>((get) => get(layoutAtom)?.customCorridors || []);

export const layoutCustomizationsMapAtom = atom<Record<number, LayoutCustomization>>((get) =>
    Object.fromEntries(
        get(layoutCustomizationsAtom)?.map((customization) => [customization.id, customization] as const) || [],
    ),
);

export const layoutCustomCorridorsMapAtom = atom<Record<number, CustomCorridor>>((get) =>
    Object.fromEntries(
        get(layoutCustomCorridorsAtom)?.map(
            (customCorridor) => [customCorridor.swingMeasuredParameterId, customCorridor] as const,
        ) || [],
    ),
);

export const convertedLayoutCustomCorridorsMapAtom = atom<Record<string, CustomCorridor & { unit: DisplayUnit }>>(
    (get) => {
        const userSettings = get(activeUserSettingsAtom);
        const swingMeasuredParameterMap = get(swingMeasuredParametersMapAtom);
        const layoutCustomCorridorsMap = get(layoutCustomCorridorsMapAtom);
        return Object.entries(layoutCustomCorridorsMap).reduce(
            (acc, [smpId, customCorridor]) => {
                const smp = swingMeasuredParameterMap[smpId];
                if (!smp) return acc;
                const { innerMin, innerMax } = customCorridor;

                const convertedMin = convertUnit({
                    value: innerMin,
                    unit: smp.units?.notation as Unit,
                    userPreferredUnit: userSettings.userPreferredUnit,
                });
                const convertedMax = convertUnit({
                    value: innerMax,
                    unit: smp.units?.notation as Unit,
                    userPreferredUnit: userSettings.userPreferredUnit,
                });
                acc[smpId] = {
                    ...customCorridor,
                    innerMin: convertedMin.value,
                    innerMax: convertedMax.value,
                    unit: convertedMin.unit as DisplayUnit,
                };
                return acc;
            },
            {} as Record<string, CustomCorridor & { unit: DisplayUnit }>,
        );
    },
);

export const layoutUpdatedAtAtom = atom((get) => {
    get(layoutAtom);
    get(layoutCustomizationsAtom);
    return new Date().toISOString();
});

export const layoutFloorModulesAtom = atom<LayoutCustomization[]>((get) => {
    const layoutCustomizations = get(layoutCustomizationsAtom);
    return (
        layoutCustomizations
            ?.filter(
                (module) =>
                    module.childUiNodeId.startsWith('graph.') ||
                    module.childUiNodeId.startsWith('module.') ||
                    module.childUiNodeId.startsWith('swing_foundations.'),
            )
            ?.filter((module) => module.showChildOn?.includes('floor'))
            .sort((a, b) =>
                !a.childDisplayOrder || !b.childDisplayOrder ? 0 : a.childDisplayOrder - b.childDisplayOrder,
            ) ?? []
    );
});

export const layoutFloorGraphLinesAtom = atom<LayoutCustomization[]>((get) => {
    const layoutCustomizations = get(layoutCustomizationsAtom);
    return (
        layoutCustomizations
            ?.filter((module) => module.childUiNodeId.startsWith('graph_line.'))
            ?.filter((module) => module.showChildOn?.includes('floor'))
            .sort((a, b) =>
                !a.childDisplayOrder || !b.childDisplayOrder ? 0 : a.childDisplayOrder - b.childDisplayOrder,
            ) ?? []
    );
});

export const ACTIVITY_NAVIGATION_NODE_ID = 'activity_navigation.default';

export const DEFAULT_KEY_PARAMETER = 'parameter.pelvis_sway_p7';

export const layoutKeyParameterAtom = atom<LayoutCustomization | undefined>((get) => {
    const layoutCustomizations = get(layoutCustomizationsAtom);
    return layoutCustomizations?.find((module) => module.parentUiNodeId === ACTIVITY_NAVIGATION_NODE_ID);
});

export const layoutKeyParameterIdAtom = atom<string>((get) => {
    const keyParameterNode = get(layoutKeyParameterAtom);
    return keyParameterNode?.childUiNodeId || DEFAULT_KEY_PARAMETER;
});

export const layoutKeyParameterNodeAtom = atom<ParameterNode | undefined>((get) => {
    const keyParameterId = get(layoutKeyParameterIdAtom);
    const parametersMap = get(parametersMapAtom);
    return parametersMap[keyParameterId];
});

export const layoutKioskModulesAtom = atom<LayoutCustomization[]>((get) => {
    const layoutCustomizations = get(layoutCustomizationsAtom);
    return (
        layoutCustomizations
            ?.filter(
                // swing foundations are omitted, because we hardcode it to render in ActivityDetails
                (module) => module.childUiNodeId.startsWith('graph.') || module.childUiNodeId.startsWith('module.'),
            )
            ?.filter((module) => module.showChildOn?.includes('kiosk'))
            .sort((a, b) =>
                !a.childDisplayOrder || !b.childDisplayOrder ? 0 : a.childDisplayOrder - b.childDisplayOrder,
            ) ?? []
    );
});

export const layoutKioskModulesMapAtom = atom<Record<string, LayoutCustomization>>((get) => {
    return Object.fromEntries(
        get(layoutKioskModulesAtom)?.map((customization) => [customization.childUiNodeId, customization] as const) ||
            [],
    );
});

const SWING_FOUNDATIONS_NODE_PREFIX = 'swing_foundations';

export const swingFoundationParameterIdsAtom = atom((get) => {
    const customizations = get(layoutCustomizationsAtom);
    return (
        customizations?.reduce<string[]>((acc, customization) => {
            if (
                !customization.parentUiNodeId?.startsWith(SWING_FOUNDATIONS_NODE_PREFIX) ||
                !customization.showChildOn?.length ||
                acc.includes(customization.childUiNodeId)
            ) {
                return acc;
            }
            acc.push(customization.childUiNodeId);
            return acc;
        }, []) ?? []
    );
});
